import React from "react";
import padStart from "lodash/padStart";
import moment from "moment";
import i18n from "~/i18n";
import Angie from "./angie.jsx";

export const stopwatchMaximum = 359999;

const getToday = () => {
  const timezone = moment.defaultZone ? moment.defaultZone.name : moment.tz.guess();
  const currentDateInTimezone = moment().tz(timezone).format("YYYY-MM-DD");
  const today = moment.utc(currentDateInTimezone);
  return today;
};

export const getCreatedRange = (range) => {
  if (!range) {
    return ``;
  }

  let start_date = moment().utc().subtract(`${range}`, `days`).format(`YYYY-MM-DD`);
  let end_date = moment().utc().format(`YYYY-MM-DD`);

  return `${start_date}:${end_date}`;
};

export const getShortDateFormat = (value) => {
  return value.replace(/Y|,$/gi, ``).trim();
};

export const formatDateSmart = (date, format, long = false) => {
  if (!date) {
    return ``;
  }

  date = moment.unix(date).utc();

  if (!long && date.year() === moment.utc().year()) {
    return date.format(getShortDateFormat(format));
  }

  return date.format(format);
};

export const dateToMySQLFormat = (timestamp, format = `YYYY-MM-DD`) => {
  return Number.isInteger(timestamp) ? moment.unix(timestamp).utc().format(format) : timestamp;
};

export const dateStringToUnixTimestamp = (date) => {
  return typeof date === `string` ? moment.utc(date).unix() : date;
};

export const getDiffBetweenDates = (startDate, dueDate, unit = `days`) => {
  if (typeof startDate === `string` && typeof dueDate === `string`) {
    return moment(dueDate).utc().diff(moment(startDate).utc(), unit);
  } else if (Number.isInteger(startDate) && Number.isInteger(dueDate)) {
    return moment.unix(dueDate).utc().diff(moment.unix(startDate).utc(), unit);
  }

  throw new TypeError(
    `getBetweenByUnit expected both values to be string or number, received: start_date: ${typeof startDate}, due_date: ${typeof dueDate}`
  );
};

export const formatLocalTime = (value, format = `HH:mm`) => {
  if (!value) {
    return ``;
  }
  return moment.unix(value).format(`${format}`);
};

export const formatLocalDate = (value, format = `YYYY-MM-DD`, long = true) => {
  if (!value) {
    return ``;
  }
  const date = moment.unix(value);
  return !long && date.year() === moment().year() ? date.format(getShortDateFormat(format)) : date.format(`${format}`);
};

export const formatLocalDateString = (currentDate, format = "D. MMM YYYY") => {
  if (!currentDate) return "";

  const date = moment(currentDate);
  const currentYear = moment().year();
  const dateYear = date.year();

  const isDayFirst = format === "D. MMM YYYY";

  const day = date.date();
  const month = date.format("MMM");

  const dayFirstFormat = `${day} ${month}`;
  const monthFirstFormat = `${month} ${day}`;

  const yearSuffix = dateYear !== currentYear ? ` ${dateYear}` : "";

  return isDayFirst ? dayFirstFormat + yearSuffix : monthFirstFormat + yearSuffix;
};

export const formatCreationDate = (value, format = `YYYY-MM-DD`, long = true) => {
  const today = getToday();
  if (moment.unix(value).format("YYYY-MM-DD") === today.clone().format("YYYY-MM-DD")) return i18n.t("Today");
  if (moment.unix(value).format("YYYY-MM-DD") === today.clone().subtract(1, "days").format("YYYY-MM-DD"))
    return i18n.t("Yesterday");
  return formatLocalDate(value, format, long);
};

// if task starts and ends on the same day, we count it as 1 day
export const getGanttDurationByDates = (startDate, dueDate) => {
  if (startDate && dueDate) {
    return getDiffBetweenDates(startDate, dueDate, `days`) + 1;
  }

  return 0;
};

export const longMonthNames = [
  i18n.t("January"),
  i18n.t("February"),
  i18n.t("March"),
  i18n.t("April"),
  i18n.t("May"),
  i18n.t("June"),
  i18n.t("July"),
  i18n.t("August"),
  i18n.t("September"),
  i18n.t("October"),
  i18n.t("November"),
  i18n.t("December"),
];

const shortMonthNames = [
  i18n.t("Jan"),
  i18n.t("Feb"),
  i18n.t("Mar"),
  i18n.t("Apr"),
  i18n.t("May"),
  i18n.t("Jun"),
  i18n.t("Jul"),
  i18n.t("Aug"),
  i18n.t("Sep"),
  i18n.t("Oct"),
  i18n.t("Nov"),
  i18n.t("Dec"),
];

const longDayNames = [
  i18n.t("Sunday"),
  i18n.t("Monday"),
  i18n.t("Tuesday"),
  i18n.t("Wednesday"),
  i18n.t("Thursday"),
  i18n.t("Friday"),
  i18n.t("Saturday"),
];

export const shortDayNames = [
  i18n.t("Sun"),
  i18n.t("Mon"),
  i18n.t("Tue"),
  i18n.t("Wed"),
  i18n.t("Thu"),
  i18n.t("Fri"),
  i18n.t("Sat"),
];

const minDayNames = [i18n.t("Su"), i18n.t("Mo"), i18n.t("Tu"), i18n.t("We"), i18n.t("Th"), i18n.t("Fr"), i18n.t("Sa")];

export const momentLocalizationObject = {
  months: longMonthNames,
  monthsShort: shortMonthNames,
  weekdays: longDayNames,
  weekdaysShort: shortDayNames,
  weekdaysMin: minDayNames,
};

// This func is mainly used in Gantt and DaySelector
// to help convert javascript Date object into
// formatted ISO 8601 date string
export const getDateStringFromDateObject = (object) => {
  let year = object.getFullYear();
  let date = padStart(`${object.getDate()}`, 2, `0`);
  let month = padStart(`${object.getMonth() + 1}`, 2, `0`);

  return `${year}-${month}-${date}`;
};

export const getMonthFromTimestamp = (timestamp) => {
  if (!timestamp) {
    return "";
  }

  const date = moment.unix(timestamp).utc();
  const month = date.month();
  const year = date.year();

  return year === moment.utc().year() ? shortMonthNames[month] : `${shortMonthNames[month]} ${year}`;
};

export const enumerateDaysBetweenDates = (startDate, endDate, removeYear = false) => {
  startDate = moment(startDate);
  endDate = moment(endDate);

  let now = startDate,
    dates = [];

  while (now.isBefore(endDate) || now.isSame(endDate)) {
    dates.push(removeYear ? now.format("MM-DD") : now.format(`YYYY-MM-DD`));
    now.add(1, `days`);
  }

  return dates;
};

export const calculateElapsed = (startedOn, alreadyElapsed = 0, offset = 0) => {
  const elapsed = alreadyElapsed + (moment().utc().unix() - offset) - startedOn;

  return elapsed > stopwatchMaximum ? stopwatchMaximum : elapsed;
};

export const withLeadingZero = (num, size = 2) => {
  let s = `${num}`;
  while (s.length < size) s = `0` + s;
  return s;
};

export const formatTime = (elapsedTime) => {
  let elapsed = moment.duration(elapsedTime, `seconds`);
  let hours = withLeadingZero(Math.floor(elapsed.asHours()));
  let minutes = withLeadingZero(elapsed.minutes());
  let seconds = withLeadingZero(elapsed.seconds());
  if (parseInt(hours) < 0) {
    hours = `00`;
  }
  if (parseInt(minutes) < 0) {
    minutes = `00`;
  }
  if (parseInt(seconds) < 0) {
    seconds = `00`;
  }
  return [hours, minutes, seconds];
};

export const formatMinutes = (value) => {
  const timeValue = `${value}`;
  const colonPosition = timeValue.indexOf(`:`);

  if (colonPosition === -1) return timeValue;

  const afterColon = timeValue.substr(colonPosition + 1);

  if (afterColon && afterColon.length > 2) {
    const roundedDecimal = parseFloat(`0.` + afterColon)
      .toFixed(2)
      .substring(2);
    return timeValue.substr(0, colonPosition) + `:` + roundedDecimal;
  } else if (afterColon.length < 2) {
    if (afterColon.length === 0) return `${timeValue}00`;
    return `${timeValue}0`;
  }

  return timeValue;
};

export const timeStringToFloat = (time) => Angie.functions.timeStringToFloat(time);

export const getFirstDayOfWeekName = (i) => longDayNames[i];

export const formatDateForDateStepper = (fromDate, toDate, dateFormat, range, firstWeekDay) => {
  const today = getToday();

  const from = moment(fromDate);
  const to = moment(toDate);
  let formattedDate;

  const isDayFirst = (dateFormat) =>
    dateFormat === "D MMM YYYY" || dateFormat === "DD/MM/YY" || dateFormat === "DD/MM/YYYY";

  const daily = (from, dateFormat) => {
    if (isDayFirst(dateFormat)) {
      if (from.isSame(today, "year")) {
        return `${from.format("DD. MMM")}`;
      }
      return `${from.format("DD. MMM YYYY")}`;
    }
    if (from.isSame(today, "year")) {
      return `${from.format("MMM DD.")}`;
    }
    return `${from.format("MMM DD. YYYY")}`;
  };

  const weekly = (from, to, dateFormat) => {
    if (isDayFirst(dateFormat)) {
      if (from.isSame(today, "year") && to.isSame(today, "year")) {
        return `${from.format("DD. MMM")} - ${to.format("DD. MMM")}`;
      }
      return `${from.format("DD. MMM YYYY")} - ${to.format("DD. MMM YYYY")}`;
    }
    if (from.isSame(today, "year") && to.isSame(today, "year")) {
      return `${from.format("MMM DD.")} - ${to.format("MMM DD.")}`;
    }
    return `${from.format("MMM DD. YYYY")} - ${to.format("MMM DD. YYYY")}`;
  };

  const monthly = (from) => {
    if (from.isSame(today, "year")) {
      return `${from.format("MMMM")}`;
    }
    return `${from.format("MMM YYYY")}`;
  };

  switch (range) {
    case "daily": {
      formattedDate = daily(from, dateFormat);
      break;
    }
    case "weekly": {
      let weekString;
      let weekNumber;
      if (firstWeekDay === 0) {
        weekNumber = moment(from).week();
      } else {
        weekNumber = moment(from).isoWeek();
      }
      weekString = weekly(from, to, dateFormat);

      formattedDate = (
        <div
          className="reports_two_rows_data"
          data-testid="two-rows-data"
        >
          <div className="reports_first_row">{weekString}</div>
          <div className="reports_second_row">
            {i18n.t("Week")} {weekNumber}
          </div>
        </div>
      );
      break;
    }
    case "monthly": {
      formattedDate = monthly(from);
      break;
    }
    case "quarterly": {
      formattedDate = `Q${from.quarter()}/${from.format("YYYY")}`;
      break;
    }
    case "yearly": {
      formattedDate = from.format("YYYY");
      break;
    }
    case "custom": {
      if (from.isSame(to, "day")) {
        formattedDate = daily(from, dateFormat);
      } else {
        formattedDate = weekly(from, to, dateFormat);
      }
      break;
    }
  }
  return formattedDate;
};

export const startOfDay = (date) => {
  if (typeof date !== `number`) {
    throw new TypeError(`Expected number, received: ${typeof date}`);
  }
  return moment.unix(date).utc().startOf("day").unix();
};

export const determinePrecision = (rawValue) => Angie.functions.determinePrecision(rawValue);

export const formatDateRange = (startDateTimestamp, dueDateTimestamp, format) => {
  if (startDateTimestamp === null || startDateTimestamp === undefined) return undefined;
  const startDate = moment.unix(startDateTimestamp).utc();
  const dueDate = moment.unix(dueDateTimestamp).utc();
  const monthFirst = format[0] === "M";
  const formatWithoutYear = format.replace(/YYYY|,$/gi, "").trim();
  const formatWithoutYearAndMonth = formatWithoutYear.replace(/MMM|,$/gi, "").trim();
  if (startDateTimestamp === dueDateTimestamp) {
    return startDate.format(formatWithoutYear);
  } else if (startDate.year() === dueDate.year() && startDate.month() === dueDate.month()) {
    return `${startDate.format(monthFirst ? formatWithoutYear : formatWithoutYearAndMonth)} - ${dueDate.format(
      monthFirst ? formatWithoutYearAndMonth : formatWithoutYear
    )}`;
  } else {
    return `${startDate.format(formatWithoutYear)} - ${dueDate.format(formatWithoutYear)}`;
  }
};

export const getDateBoundaries = (dateFilter) => {
  switch (dateFilter) {
    case "this_week":
      return {
        from: moment().utc().startOf("week"),
        to: moment().utc().endOf("week"),
      };
    case "last_week":
      return {
        from: moment().utc().subtract(7, "days").startOf("week"),
        to: moment().utc().subtract(7, "days").endOf("week"),
      };
    case "this_month":
      return {
        from: moment().utc().startOf("month"),
        to: moment().utc().endOf("month"),
      };
    case "last_month":
      return {
        from: moment().utc().subtract(1, "month").startOf("month"),
        to: moment().utc().subtract(1, "month").endOf("month"),
      };
    case "this_quarter":
      return {
        from: moment().utc().startOf("quarter"),
        to: moment().utc().endOf("quarter"),
      };
    case "last_quarter":
      return {
        from: moment().utc().subtract(1, "quarter").startOf("quarter"),
        to: moment().utc().subtract(1, "quarter").endOf("quarter"),
      };
    case "this_year":
      return {
        from: moment().utc().startOf("year"),
        to: moment().utc().endOf("year"),
      };
    case "last_year":
      return {
        from: moment().utc().subtract(1, "year").startOf("year"),
        to: moment().utc().subtract(1, "year").endOf("year"),
      };
    default:
      return false;
  }
};

export const filterByDate = (createdOn, dateFilter) => {
  const recordDate = moment.unix(createdOn).utc();
  const { from: fromDate, to: toDate } = getDateBoundaries(dateFilter);

  return recordDate.isBetween(fromDate, toDate, undefined, "[]");
};
